<script setup>
import { ref, watch } from 'vue';
import { useStore } from 'vuex';
import logger from 'shared/helpers/logger';
import PipelineCardsAPI from 'dashboard/api/pipelineCards';
import PipelineCardDetails from './PipelineCardDetails.vue';

const props = defineProps({
  itemId: {
    type: [Number, String],
    required: true,
  },
  forceReload: {
    type: Number,
    default: 0,
  },
});

const emit = defineEmits(['back', 'itemUpdated']);

const store = useStore();

const item = ref(null);
const isLoading = ref(true);

// `silent` skips the full-screen loading spinner. Used by handleItemUpdated so
// that in-place updates (status change, notes, assignee…) reconcile in the
// background WITHOUT blanking the whole card detail on every mutation. The
// initial load / item switch (the watch below) keeps the spinner.
const fetchItemDetails = async ({ silent = false } = {}) => {
  try {
    if (!silent) isLoading.value = true;
    const { data } = await PipelineCardsAPI.getCard(props.itemId);
    item.value = data;
  } catch (error) {
    logger.error(
      '[DEBUG PipelineCardDetailView] fetchItemDetails - Erro:',
      error
    );
  } finally {
    if (!silent) isLoading.value = false;
  }
};

// Watch após a definição da função
watch(
  [() => props.itemId, () => props.forceReload],
  () => {
    // Limpar estado atual antes de buscar novo item
    item.value = null;
    logger.debug(
      '[DEBUG PipelineCardDetailView] Estados limpos antes de buscar novo item'
    );

    logger.debug('[DEBUG PipelineCardDetailView] Fetching new item details...');
    fetchItemDetails();
  },
  { immediate: true }
);

const handleBack = () => {
  logger.debug('[DEBUG PipelineCardDetailView] handleBack - Iniciando...');
  logger.debug(
    '[DEBUG PipelineCardDetailView] handleBack - Item atual:',
    item.value
  );
  // Limpar estados
  item.value = null;

  logger.debug('[DEBUG PipelineCardDetailView] handleBack - Estados limpos');
  emit('back');
};

const handleItemUpdated = async patch => {
  // `patch` is the authoritative payload returned by the deal-status endpoints
  // (mark_won / mark_lost / reopen): { id, status, pipeline_stage, won_at,
  // lost_at, lost_reason, won_value, ... }. Apply it to BOTH the open card and
  // the board store synchronously so neither shows a stale status while the
  // background refetch is in flight — this is what fixes the long-standing
  // "muda o status com o card aberto e não persiste / volta ao anterior" bug.
  if (patch && typeof patch === 'object' && patch.id != null) {
    item.value = { ...item.value, ...patch };
    store.commit('pipelineCards/UPDATE_PIPELINE_CARD_ITEM', patch);
  }
  emit('itemUpdated');
  // Silent reconcile (activities, derived fields). No spinner: the card stays
  // visible and already shows the patched status.
  await fetchItemDetails({ silent: true });
};

const handleDetailsDeleted = () => {
  emit('itemUpdated');
  emit('back');
};
</script>

<template>
  <div class="flex flex-col h-full w-full bg-n-solid-2 overflow-hidden">
    <!-- Loading State -->
    <div v-if="isLoading" class="flex-1 flex justify-center items-center py-12">
      <span
        class="w-8 h-8 border-2 border-t-n-brand border-r-n-brand border-b-transparent border-l-transparent rounded-full animate-spin"
      />
    </div>

    <!-- Content -->
    <div v-else class="flex-1 min-h-0 overflow-hidden">
      <!-- Visualização inline (all editing is done inline) -->
      <PipelineCardDetails
        v-if="item"
        :item="item"
        @close="handleBack"
        @item-updated="handleItemUpdated"
        @deleted="handleDetailsDeleted"
      />
    </div>
  </div>
</template>
